import { useDaaSEditPermission } from '@internal/plugin-azure-resources-common-react';
import React from 'react';

import { stringifyEntityRef } from '@backstage/catalog-model';
import { IconLinkVerticalProps } from '@backstage/core-components';
import { EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph';
import { useEntity } from '@backstage/plugin-catalog-react';

import { Grid } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';

import { ArgoCDLogoMedium } from '../../home/ArgoCDLogo';
import { AboutCard } from '../Components/AboutCard';
import {
  ArgocdProjectStatusCard,
  ArgocdApplicationStatusCard,
  ArgocdApplicationSetStatusCard,
} from '../Components/ArgocdStatusCard';
import {
  DaasProjectContentCard,
  DaasApplicationContentCard,
  DaasApplicationSetContentCard,
} from '../Components/DaasContentCard';
import {
  isDaaSProject,
  isDaaSApplication,
  isDaaSApplicationSet,
} from '../entityUtils';
import { ActionContent } from './ActionContent';
import { EntityWarningContent } from './EntityWarningContent';

export const DaaSOverviewContent = () => {
  const { entity } = useEntity();
  const { isSecurityGroupMember } = useDaaSEditPermission(entity);

  const viewArgoLink: IconLinkVerticalProps = {
    label: 'Argo CD',
    disabled: !entity.metadata.annotations?.['tomtom.com/daas-argocd-url'],
    icon: <ArgoCDLogoMedium />,
    href: entity.metadata.annotations?.['tomtom.com/daas-argocd-url'],
  };

  const createAppLink: IconLinkVerticalProps = {
    label: 'Create DaaS App',
    icon: <AddIcon />,
    href: '/create/templates/default/daas-application',
  };

  const createAppSetLink: IconLinkVerticalProps = {
    label: 'Create DaaS AppSet',
    icon: <AddIcon />,
    href: '/create/templates/default/daas-application-set',
  };

  const additionalLinks = isDaaSProject(entity)
    ? [viewArgoLink, createAppLink, createAppSetLink]
    : [viewArgoLink];

  let editTemplateFormData = {};
  switch (entity?.spec?.type) {
    case 'daas-application':
      editTemplateFormData = {
        applicationData: {
          applicationRef: stringifyEntityRef(entity),
        },
      };
      break;
    case 'daas-application-set':
      editTemplateFormData = {
        applicationSetData: {
          applicationSetRef: stringifyEntityRef(entity),
        },
      };
      break;
    case 'daas-project':
      editTemplateFormData = {
        projectData: {
          projectRef: stringifyEntityRef(entity),
        },
      };
      break;
    default:
      editTemplateFormData = {};
      break;
  }

  return (
    <Grid container spacing={3} alignItems="stretch">
      <EntityWarningContent />
      {ActionContent}

      <Grid item md={6}>
        <AboutCard
          variant="gridItem"
          hideTechDocsLinks
          canEdit={isSecurityGroupMember}
          editUrl={`/create/templates/default/edit-${
            entity?.spec?.type
          }?formData=${encodeURIComponent(
            JSON.stringify(editTemplateFormData),
          )}`}
          canDelete={isSecurityGroupMember}
          deleteTemplateUrl={`/create/templates/default/daas-delete-component?formData=${encodeURIComponent(
            JSON.stringify({
              component: {
                type: entity?.spec?.type || 'daas-project',
                entityRef: stringifyEntityRef(entity),
              },
            }),
          )}`}
          additionalSubHeaderLinks={additionalLinks}
        />
      </Grid>

      {isDaaSProject(entity) && (
        <Grid item md={6} xs={12}>
          <ArgocdProjectStatusCard variant="gridItem" />
        </Grid>
      )}

      {isDaaSApplication(entity) && (
        <Grid item md={6} xs={12}>
          <ArgocdApplicationStatusCard variant="gridItem" />
        </Grid>
      )}

      {isDaaSApplicationSet(entity) && (
        <Grid item md={6} xs={12}>
          <ArgocdApplicationSetStatusCard variant="gridItem" />
        </Grid>
      )}

      {isDaaSProject(entity) && (
        <Grid item md={12} xs={12}>
          <DaasProjectContentCard variant="gridItem" />
        </Grid>
      )}

      {isDaaSApplication(entity) && (
        <Grid item md={12} xs={12}>
          <DaasApplicationContentCard variant="gridItem" />
        </Grid>
      )}

      {isDaaSApplicationSet(entity) && (
        <Grid item md={12} xs={12}>
          <DaasApplicationSetContentCard variant="gridItem" />
        </Grid>
      )}

      <Grid item md={12} xs={12}>
        <EntityCatalogGraphCard variant="gridItem" height={400} />
      </Grid>
    </Grid>
  );
};
